import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';
import {
  HomeIcon,
  ComputerDesktopIcon,
  TicketIcon,
  UsersIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  ArrowsRightLeftIcon,
  BookOpenIcon,
  KeyIcon,
  WrenchScrewdriverIcon,
  SunIcon,
  MoonIcon,
  CircleStackIcon,
  ChatBubbleLeftRightIcon,
  SignalIcon,
  TvIcon
} from '@heroicons/react/24/outline';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, isIT } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Ana Sayfa', href: '/', icon: HomeIcon, roles: ['admin', 'it', 'user'] },
    { name: 'Cihazlar', href: '/devices', icon: ComputerDesktopIcon, roles: ['admin', 'it'] },
    { name: 'Talepler', href: '/requests', icon: TicketIcon, roles: ['admin', 'it', 'user'] },
    { name: 'Çözülen İşlemler', href: '/resolved-requests', icon: DocumentCheckIcon, roles: ['admin', 'it', 'user'] },
    { name: 'Canias Talepleri', href: '/canias-requests', icon: CircleStackIcon, roles: ['admin', 'it', 'user'] },
    { name: 'Lisanslar', href: '/licenses', icon: KeyIcon, roles: ['admin', 'it'] },
    { name: 'Bakım Takibi', href: '/maintenances', icon: WrenchScrewdriverIcon, roles: ['admin', 'it'] },
    { name: 'Değişim Algoritması', href: '/replacement-algorithm', icon: ArrowsRightLeftIcon, roles: ['admin', 'it'] },
    { name: 'Kullanıcılar', href: '/users', icon: UsersIcon, roles: ['admin'] },
    { name: 'Tanımlamalar', href: '/settings', icon: Cog6ToothIcon, roles: ['admin'] },
    { name: 'Mesajlar', href: '/chat', icon: ChatBubbleLeftRightIcon, roles: ['admin', 'it', 'user'] },
    { name: 'Çevrimiçi Kullanıcılar', href: '/online-users', icon: SignalIcon, roles: ['admin', 'it'] },
    { name: 'MimDesk', href: '/mimdesk', icon: TvIcon, roles: ['admin', 'it'] },
  ];

  const filteredNavigation = navigation.filter(item => item.roles.includes(user?.role));

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white dark:bg-gray-800">
          <div className="flex h-20 items-center justify-between px-4 border-b dark:border-gray-700">
            <div className="flex items-center">
              <img src="/mimb_logo.png" alt="Logo" className="h-12 w-auto mr-2" />
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">IT Envanter</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="dark:text-gray-300">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                  isActive(item.href)
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            ))}
            <a
              href="/kullanim-kilavuzu.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 dark:text-purple-300 dark:bg-purple-900 dark:hover:bg-purple-800 mt-4"
            >
              <BookOpenIcon className="h-5 w-5 mr-3" />
              Kullanım Kılavuzu
            </a>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r dark:border-gray-700">
          <div className="flex flex-col items-center px-4 py-4 border-b dark:border-gray-700">
            <img src="/mimb_logo.png" alt="Logo" className="h-16 w-auto mb-2" />
            <span className="text-xl font-bold text-blue-600 dark:text-blue-400">IT Envanter Sistemi</span>
          </div>
          <nav className="flex-1 px-2 py-4 space-y-1">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                  isActive(item.href)
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="border-t dark:border-gray-700 p-4">
            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={toggleDarkMode}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title={darkMode ? 'Açık Mod' : 'Koyu Mod'}
              >
                {darkMode ? (
                  <SunIcon className="h-5 w-5 mr-2 text-yellow-500" />
                ) : (
                  <MoonIcon className="h-5 w-5 mr-2 text-gray-600" />
                )}
                {darkMode ? 'Açık' : 'Koyu'}
              </button>
              <NotificationBell />
            </div>
            {/* Bildirimler ve Kullanim Kilavuzu */}
            <div className="flex items-center justify-between mb-3">
              <a
                href="/kullanim-kilavuzu.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 dark:text-purple-300 dark:bg-purple-900 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-800 transition-colors"
              >
                <BookOpenIcon className="h-5 w-5 mr-2" />
                Kılavuz
              </a>
            </div>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                  {user?.fullName?.charAt(0) || 'U'}
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{user?.fullName}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-500"
                title="Çıkış"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
            {/* Hazırlayan */}
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className="text-xs text-gray-400 dark:text-gray-500">Hazırlayan</p>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Oğuz SULTANOĞLU</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex h-16 bg-white dark:bg-gray-800 border-b dark:border-gray-700 lg:hidden">
          <button
            className="px-4 text-gray-500 dark:text-gray-400"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex flex-1 items-center justify-between px-4">
            <div className="flex items-center">
              <img src="/mimb_logo.png" alt="Logo" className="h-8 w-auto mr-2" />
              <span className="text-lg font-semibold text-blue-600 dark:text-blue-400">IT Envanter</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                title={darkMode ? 'Açık Mod' : 'Koyu Mod'}
              >
                {darkMode ? (
                  <SunIcon className="h-6 w-6 text-yellow-500" />
                ) : (
                  <MoonIcon className="h-6 w-6" />
                )}
              </button>
              <NotificationBell />
              <button
                onClick={handleLogout}
                className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-500"
              >
                <ArrowRightOnRectangleIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
